import { BlogPost } from '@/types/blog';
import coverImage from '@/assets/blog-ci-solar-cover.jpg';

export const post: BlogPost = {
  slug: "going-all-in-on-clean-energy",
  title: "We're Going All In on Clean Energy",
  excerpt: "Focus is a startup's ace. Fordje is going all in on clean energy — the industry we come from and the problem we've lived over and over.",
  content: `
    <p>
      Focus is a startup's ace. For a while, we'd been treating it like a face card.
    </p>

    <p>
      Fordje was built to solve the complexities of local regulation across the built environment — commercial building, homebuilding, all of it. We still believe that's where we're headed. But belief in a big vision and discipline about where you win first are two different things, and this year the second one got loud.
    </p>

    <p>
      Our experience in Atlanta with the <a href="https://coxcleantech.com/accelerator/" target="_blank" rel="noopener noreferrer" class="text-fordje-blue hover:text-fordje-accent underline">Cox Cleantech Accelerator</a>, the clear pull from developers and consultants across C&amp;I and residential clean energy, and some pointed nudging from people who know this market made the call clear. And frankly, easy. It's the industry we come from. The problem we're solving is one we've lived over and over. So we're focusing on it.
    </p>

    <h2>Why clean energy, why now</h2>

    <p>
      The world needs energy, fast. Clean energy is the best way forward.
    </p>

    <p>
      But building it is slower and more expensive than it should be — not because the technology isn't ready, but because the path from sale to switched-on is full of regulatory uncertainty. Every week of delay is capital sitting idle and a customer getting cold feet. If you can get projects to revenue faster and take cost out of every watt, you don't just help one developer. You help the whole transition move.
    </p>

    <p>
      That's the work. And removing the delays and friction that uncertainty breeds is exactly what we do.
    </p>

    <h2>Permitting software solved the wrong half of the problem</h2>

    <p>
      Here's what we keep seeing.
    </p>

    <p>
      Permitting software has done its job. Submitting a permit, and getting it approved or rejected, is faster than ever. <a href="https://www.sciencedirect.com/science/article/pii/S0301421520303517" target="_blank" rel="noopener noreferrer" class="text-fordje-blue hover:text-fordje-accent underline">NREL and DOE's 2020 rooftop PV analysis</a> found as much: permit durations have stabilized thanks to streamlining, even as customers still face long and uncertain timelines.
    </p>

    <p>
      But speed at the permit desk doesn't help if the information going in is wrong. A faster "no" is still a "no." The bottleneck moved upstream, to figuring out what each jurisdiction actually requires before you submit.
    </p>

    <p>
      And that gap is expensive. <a href="https://seia.org/wp-content/uploads/2019/05/Solar-Soft-Costs-Factsheet-1.pdf" target="_blank" rel="noopener noreferrer" class="text-fordje-blue hover:text-fordje-accent underline">A 2019 SEIA study</a> found that $0.87 of the then-$3/watt average price for rooftop solar came from the indirect costs of permitting: the labor to research local requirements, and the delays and cancellations that follow when you get them wrong.
    </p>

    <p>
      The cancellations are the part that stings most:
    </p>

    <blockquote class="border-l-4 border-fordje-blue pl-6 py-2 my-6 italic text-xl md:text-2xl text-fordje-navy">
      "Based on data from our members, SEIA estimates that a one week delay in system installation due to permitting, inspection and interconnection processes increases the client cancellation rate by 10%."
    </blockquote>

    <p>
      One week. Ten percent. That's not a paperwork problem. That's revenue walking out the door.
    </p>

    <h2>What Fordje does about it</h2>

    <p>
      We don't replace permitting software. We complement it.
    </p>

    <p>
      Fordje helps teams get local regulatory requirements right at the point of sale and design, the moment where a wrong assumption sets up a rejection weeks later. When a project reaches permitting or interconnection, the hard part is already handled, and it keeps flying through.
    </p>

    <p>
      Get it right early, and the delays, the rework, and the cancellations that come from getting it wrong simply don't happen. That's how you get to revenue faster and take cost out of every watt.
    </p>

    <p>
      This is the problem we know best. It's the one we've lived. And it's the one we're all in on.
    </p>

    <h2>Want to get to revenue faster?</h2>

    <p>
      <a href="/request-demo" class="text-fordje-blue hover:text-fordje-accent underline">Book a demo</a> and see how Fordje gets your projects right the first time — from sale to switched-on.
    </p>
  `,
  coverImage: coverImage,
  date: "2026-07-20T12:00:00",
  readTime: 4,
  categories: ["Company News", "Clean Energy"],
  author: {
    name: "Gillian Sowray",
    title: "CEO & Co-Founder",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  },
  metaDescription: "Fordje is going all in on clean energy. Here's why focusing on the sale-and-design stage of solar, storage, and EV projects is how we help the transition move faster.",
  seoKeywords: ["clean energy", "solar permitting", "soft costs", "regulatory uncertainty", "Fordje"]
};
